/**
 * Shared helpers for guided event schedule form ↔ API field mapping.
 */

import type { EventScheduleMode, GuidedScheduleInferredMode } from './event-schedule.types';

export type { EventScheduleMode, GuidedScheduleInferredMode };

export function hasAdditionalDates(data: Record<string, unknown>): boolean {
  if (hasSeriesPlannerDates(data)) {
    return true;
  }
  const additional = data['additional_dates'];
  if (Array.isArray(additional)) {
    return additional.length > 0;
  }
  if (!('additional_dates' in data) && Array.isArray(data['sub_events'])) {
    return (data['sub_events'] as unknown[]).length > 0;
  }
  return false;
}

export function parseJsonArrayField(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function parseSeriesDateEntries(
  data: Record<string, unknown>
): SeriesDateEntry[] {
  return parseJsonArrayField(data['series_dates'])
    .map((entry) => {
      if (typeof entry === 'string') {
        const date = toDateOnly(entry);
        return date ? { date } : null;
      }
      if (entry && typeof entry === 'object') {
        const row = entry as SeriesDateEntry;
        const date = toDateOnly(row.date);
        if (!date) {
          return null;
        }
        return {
          date,
          series_date_guid: row.series_date_guid,
        };
      }
      return null;
    })
    .filter((entry): entry is SeriesDateEntry => entry != null);
}

export function hasSeriesPlannerDates(data: Record<string, unknown>): boolean {
  if (data['schedule_mode'] !== 'multi_dates') {
    return false;
  }
  if (data['series_planner_active'] === 'true') {
    return true;
  }
  return parseSeriesDateEntries(data).length > 0;
}

export function parseSeriesDayLocationsMap(
  data: Record<string, unknown>
): Record<string, SeriesLocationDefaults> {
  const raw = data['series_day_locations'];
  let parsed: unknown = raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }
  const result: Record<string, SeriesLocationDefaults> = {};
  for (const [dateKey, value] of Object.entries(parsed as Record<string, unknown>)) {
    const date = toDateOnly(dateKey);
    if (!date || !value || typeof value !== 'object') {
      continue;
    }
    const v = value as Record<string, unknown>;
    result[date] = {
      location: String(v['location'] || ''),
      room: String(v['room'] || ''),
      manual_location: String(v['manual_location'] || ''),
      manual_location_address: String(v['manual_location_address'] || ''),
      manual_room_capacity: String(v['manual_room_capacity'] || ''),
    };
  }
  return result;
}

function parseSeriesDayTimesMap(
  data: Record<string, unknown>
): Record<string, { start: string; end: string }> {
  const raw = data['series_day_times'];
  let parsed: unknown = raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }
  const result: Record<string, { start: string; end: string }> = {};
  for (const [dateKey, value] of Object.entries(parsed as Record<string, unknown>)) {
    const date = toDateOnly(dateKey);
    if (!date || !value || typeof value !== 'object') {
      continue;
    }
    const v = value as { start?: unknown; end?: unknown };
    result[date] = {
      start: normalizeTime(String(v.start || '09:00')),
      end: normalizeTime(String(v.end || '16:00')),
    };
  }
  return result;
}

function parseSeriesDayModulesMap(
  data: Record<string, unknown>
): Record<string, { module_ids: number[]; subtype_ids: number[] }> {
  const raw = data['series_day_modules'];
  let parsed: unknown = raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }
  const result: Record<string, { module_ids: number[]; subtype_ids: number[] }> =
    {};
  for (const [dateKey, value] of Object.entries(parsed as Record<string, unknown>)) {
    const date = toDateOnly(dateKey);
    if (!date || !value || typeof value !== 'object') {
      continue;
    }
    const v = value as { module_ids?: unknown; subtype_ids?: unknown };
    const moduleIds = Array.isArray(v.module_ids)
      ? v.module_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    const subtypeIds = Array.isArray(v.subtype_ids)
      ? v.subtype_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    result[date] = { module_ids: moduleIds, subtype_ids: subtypeIds };
  }
  return result;
}

function buildItemsFromDayModules(
  moduleIds: number[],
  subtypeIds: number[]
): Array<{ item_type: string; item_id: number; deleted: boolean }> {
  const items: Array<{ item_type: string; item_id: number; deleted: boolean }> =
    [];
  for (const id of moduleIds) {
    items.push({ item_type: 'modules', item_id: id, deleted: false });
  }
  for (const id of subtypeIds) {
    items.push({ item_type: 'subtypes', item_id: id, deleted: false });
  }
  return items;
}

function parseSurveyIdListField(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0);
      }
    } catch {
      return [];
    }
  }
  return [];
}

function applyPerDayModulesToRows(
  rows: SeriesDateRow[],
  data: Record<string, unknown>
): SeriesDateRow[] {
  const map = parseSeriesDayModulesMap(data);
  const sameModules = data['series_same_modules'] !== 'false';
  const defaultModuleIds = parseSurveyIdListField(
    data['series_default_module_ids']
  );
  const defaultSubtypeIds = parseSurveyIdListField(
    data['series_default_subtype_ids']
  );
  if (
    Object.keys(map).length === 0 &&
    (!sameModules ||
      (defaultModuleIds.length === 0 && defaultSubtypeIds.length === 0))
  ) {
    return rows;
  }
  return rows.map((row) => {
    const date = toDateOnly(row.sub_event_date);
    let day = date ? map[date] : undefined;
    if (!day && sameModules && date) {
      day = {
        module_ids: defaultModuleIds,
        subtype_ids: defaultSubtypeIds,
      };
    }
    if (!day) {
      return row;
    }
    const items = buildItemsFromDayModules(day.module_ids, day.subtype_ids);
    if (items.length === 0) {
      return row;
    }
    return { ...row, items };
  });
}

export function isSameCalendarDay(start?: string, end?: string): boolean {
  const startDay = toDateOnly(start);
  const endDay = toDateOnly(end);
  if (!startDay) {
    return true;
  }
  if (!endDay) {
    return true;
  }
  return startDay === endDay;
}

/** Infer API duration type from guided form fields. */
export function inferGuidedScheduleMode(data: Record<string, unknown>): EventScheduleMode | '' {
  if (data['schedule_mode'] === 'multi_dates') {
    return hasSeriesPlannerDates(data) ? 'multiple_dates_locations' : '';
  }
  if (hasAdditionalDates(data)) {
    return 'multiple_dates_locations';
  }
  const start = String(data['start_date'] || '');
  if (!start) {
    return '';
  }
  const end = String(data['end_date'] || '');
  if (!end || isSameCalendarDay(start, end)) {
    return 'single_day';
  }
  return 'multi_day';
}

/** Infer UI-only mode for visibleIf (single_day | multi_day | series). */
export function inferGuidedScheduleInferredMode(
  data: Record<string, unknown>
): GuidedScheduleInferredMode {
  if (hasSeriesPlannerDates(data)) {
    return 'series';
  }
  if (hasAdditionalDates(data)) {
    return 'series';
  }
  const start = String(data['start_date'] || '');
  const end = String(data['end_date'] || '');
  if (!start) {
    return '';
  }
  if (!end || isSameCalendarDay(start, end)) {
    return 'single_day';
  }
  return 'multi_day';
}

/** True when start_date and end_date fall on different calendar days. */
export function inferScheduleCrossesDays(data: Record<string, unknown>): boolean {
  const start = String(data['start_date'] || '');
  const end = String(data['end_date'] || '');
  if (!start || !end) {
    return false;
  }
  return !isSameCalendarDay(start, end);
}

export function getScheduleMode(data: Record<string, unknown>): EventScheduleMode | '' {
  const guided = inferGuidedScheduleMode(data);
  if (guided) {
    return guided;
  }

  const legacy = data['event_duration_type'];
  if (typeof legacy === 'string' && legacy) {
    return normalizeScheduleModeForForm(legacy) as EventScheduleMode | '';
  }

  return '';
}

export function normalizeScheduleModeForForm(mode: string): EventScheduleMode | '' {

  if (mode === 'multiple_dates_locations') {

    return 'multiple_dates';

  }

  if (

    mode === 'single_day' ||

    mode === 'multi_day' ||

    mode === 'multiple_dates'

  ) {

    return mode as EventScheduleMode;

  }

  return '';

}

export function getSeriesShareParticipants(data: Record<string, unknown>): boolean {

  const flat = data['series_share_participants'];

  if (flat === false || flat === 'false') {

    return false;

  }

  if (flat === true || flat === 'true') {

    return true;

  }

  return false;

}

export function toDatetimeLocalInput(dateTime?: string): string {

  if (!dateTime) {

    return '';

  }

  const day = toDateOnly(dateTime);

  if (!day) {

    return '';

  }

  return `${day}T${extractTimeFromDateTime(dateTime)}`;

}

function buildPrimaryRowFromGuidedForm(data: Record<string, unknown>): SeriesDateRow {

  const start = String(data['start_date'] || '');

  const end = String(data['end_date'] || start);

  const row: SeriesDateRow = {

    ...splitRowToFormFields(start, extractTimeFromDateTime(end)),

  };

  if (data['primary_session_guid']) {
    row.sub_event_guid = String(data['primary_session_guid']);
  }

  const mainCatering = String(data['catering'] || 'irrelevant');
  row.sub_event_catering = mainCatering;

  if (data['location']) {

    row.sub_event_location = String(data['location']);

    row.sub_event_room = data['room'] ? String(data['room']) : undefined;

    row.sub_event_manual_location = data['manual_location'] ? String(data['manual_location']) : undefined;

    row.sub_event_manual_location_address = data['manual_location_address']

      ? String(data['manual_location_address'])

      : undefined;

    row.sub_event_manual_room_capacity = data['manual_room_capacity'] as string | number | undefined;

  }

  return row;

}

function applyGuidedExceptionsToRows(

  rows: SeriesDateRow[],

  exceptions: SeriesException[]

): SeriesDateRow[] {

  if (!exceptions.length) {

    return rows;

  }

  const result: SeriesDateRow[] = [];

  for (const row of rows) {

    const dateOnly = toDateOnly(row.sub_event_date);

    const exc = findExceptionForDate(exceptions, dateOnly);

    if (isSkipException(exc)) {

      continue;

    }

    const updated: SeriesDateRow = { ...row };

    if (exc) {

      if (exc.exception_start_time) {

        updated.sub_event_start_time = normalizeTime(exc.exception_start_time);

      }

      if (exc.exception_end_time) {

        updated.sub_event_end_time = normalizeTime(exc.exception_end_time);

      }

      const excLocation = hasLocationOverride(exc) ? readExceptionLocation(exc) : null;

      if (excLocation) {

        applyLocationDefaultsToRow(updated, excLocation);

      }

      if (exc?.exception_additional_rooms?.length) {
        updated.sub_event_additional_rooms = exc.exception_additional_rooms.map((row) => ({ ...row }));
      }

      if (exc.exception_sub_event_guid) {

        updated.sub_event_guid = exc.exception_sub_event_guid;

      }

    }

    result.push(updated);

  }

  return result;

}

/** Build per-day API rows from guided form (primary session + additional_dates). */

export function buildSeriesRowsFromGuidedForm(data: Record<string, unknown>): SeriesDateRow[] {

  if (hasSeriesPlannerDates(data)) {
    const entries = parseSeriesDateEntries(data);
    if (entries.length > 0) {
      const work: Record<string, unknown> = {
        ...data,
        series_dates: entries,
        series_planning_mode:
          data['series_planning_mode'] || 'standard_with_exceptions',
      };
      let rows = expandStandardPlanToSubEvents(
        'multiple_dates_locations',
        work
      );
      rows = applyPerDayModulesToRows(rows, data);
      rows = resolveSeriesRowCatering(
        rows,
        String(data['catering'] || data['series_default_catering'] || 'irrelevant')
      );
      rows = applyPerDayCateringToRows(rows, data);
      return rows;
    }
  }

  const primary = buildPrimaryRowFromGuidedForm(data);

  const additional = ((data['additional_dates'] as SeriesDateRow[]) || []).map((row) => ({ ...row }));

  let rows = [primary, ...additional];

  rows = resolveSeriesRowLocations(rows);
  rows = resolveSeriesRowCatering(rows, String(data['catering'] || 'irrelevant'));
  rows = applyPerDayCateringToRows(rows, data);

  const exceptions =
    inferScheduleCrossesDays(data) ? (data['series_exceptions'] as SeriesException[]) || [] : [];

  if (exceptions.length > 0) {

    rows = applyGuidedExceptionsToRows(rows, exceptions);

  }

  return rows;

}

/** Map API event rows into guided edit form fields. */

export function mapApiRowsToGuidedForm(

  apiType: EventScheduleMode,

  startDate?: string,

  endDate?: string,

  rows: SeriesDateRow[] = []

): {

  start_date: string;

  end_date: string;

  additional_dates: SeriesDateRow[];

  schedule_inferred_mode: GuidedScheduleInferredMode;

} {

  if (apiType === 'single_day') {

    const start = toDatetimeLocalInput(startDate);

    const endTime = extractTimeFromDateTime(endDate);

    return {

      start_date: start,

      end_date: combineDateAndTime(toDateOnly(startDate), endTime),

      additional_dates: [],

      schedule_inferred_mode: 'single_day',

    };

  }

  if (apiType === 'multi_day') {

    return {

      start_date: toDatetimeLocalInput(startDate),

      end_date: toDatetimeLocalInput(endDate),

      additional_dates: [],

      schedule_inferred_mode: 'multi_day',

    };

  }

  const merged = mergeSeriesRowsForForm(apiType, startDate, endDate, rows);

  const allRows = merged.rows;

  if (!allRows.length) {

    return {

      start_date: toDatetimeLocalInput(startDate),

      end_date: toDatetimeLocalInput(endDate),

      additional_dates: [],

      schedule_inferred_mode: 'series',

    };

  }

  const primary = allRows[0];

  const primaryRow = allRows[0];
  const additionalWithLoc = applySeriesRowLocationsToFormRows(
    allRows.slice(1),
    primaryRow
  );
  const additional = applySeriesRowCateringToFormRows(
    additionalWithLoc,
    primaryRow
  );

  return {

    start_date: combineDateAndTime(primary.sub_event_date, primary.sub_event_start_time || '12:00'),

    end_date: combineDateAndTime(primary.sub_event_date, primary.sub_event_end_time || '13:00'),

    additional_dates: additional,

    schedule_inferred_mode: 'series',

  };

}

export function copyRowLocationToParentFormFields(

  data: Record<string, unknown>,

  row: SeriesDateRow

): void {

  if (!row.sub_event_location) {

    return;

  }

  data['location'] = row.sub_event_location;

  data['room'] = row.sub_event_room ?? '';

  data['manual_location'] = row.sub_event_manual_location ?? '';

  data['manual_location_address'] = row.sub_event_manual_location_address ?? '';

  data['manual_room_capacity'] = row.sub_event_manual_room_capacity ?? '';

}

export function formatLocalDateTime(date: Date): string {

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, '0');

  const day = String(date.getDate()).padStart(2, '0');

  const hours = String(date.getHours()).padStart(2, '0');

  const minutes = String(date.getMinutes()).padStart(2, '0');

  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

}

/** Combine YYYY-MM-DD date with HH:mm time into local datetime string. */

export function combineDateAndTime(datePart: string, timePart: string): string {

  const dateOnly = datePart.includes('T') ? datePart.split('T')[0] : datePart.substring(0, 10);

  const time = (timePart || '12:00').substring(0, 5);

  return `${dateOnly}T${time}:00`;

}

/** Apply session end time to a start datetime string. */

export function applyEndTimeToStartDate(startDateTime: string, endTime: string): string {

  const start = new Date(startDateTime);

  const endDateTime = new Date(start);

  const parts = (endTime || '13:00').split(':');

  endDateTime.setHours(parseInt(parts[0], 10) || 0, parseInt(parts[1], 10) || 0, 0, 0);

  return formatLocalDateTime(endDateTime);

}

/** Keep end_date valid when start_date moves ahead — copy start if empty, else move end to start's date. */
export function syncEndDateFromStart(startDateTime: string, endDateTime?: string): string {
  const start = String(startDateTime);
  const end = String(endDateTime || '');
  if (!end) {
    return start;
  }
  if (new Date(start) <= new Date(end)) {
    return end;
  }
  const endTime = extractTimeFromDateTime(end);
  let newEnd = applyEndTimeToStartDate(start, endTime);
  if (new Date(newEnd) <= new Date(start)) {
    const bumped = new Date(start);
    bumped.setHours(bumped.getHours() + 1);
    newEnd = formatLocalDateTime(bumped);
  }
  return newEnd;
}

export interface SeriesDateRow {

  sub_event_guid?: string;

  sub_event_date: string;

  sub_event_start_time?: string;

  sub_event_end_time?: string;

  sub_event_location?: string;

  sub_event_room?: string;

  sub_event_manual_location?: string;

  sub_event_manual_location_address?: string;

  sub_event_manual_room_capacity?: string | number;

  sub_event_additional_rooms?: Array<{ sub_extra_room?: string; extra_room?: string }>;

  same_as_previous?: boolean | string;

  sub_event_catering?: string;

  same_catering_as_previous?: boolean | string;

  items?: Array<{ item_type: string; item_id: number; deleted?: boolean }>;

}

export type SeriesPlanningMode = 'standard_with_exceptions' | 'manual';

export interface SeriesDateEntry {

  date: string;

  series_date_guid?: string;

}

export interface SeriesException {

  exception_date: string;

  exception_start_time?: string;

  exception_end_time?: string;

  exception_location?: string;

  exception_room?: string;

  exception_manual_location?: string;

  exception_manual_location_address?: string;

  exception_manual_room_capacity?: string | number;

  exception_additional_rooms?: Array<{ exception_extra_room?: string; extra_room?: string }>;

  exception_sub_event_guid?: string;

  exception_skip?: boolean;

}

export interface SeriesStandardPlan {

  planningMode: SeriesPlanningMode;

  series_default_start_time: string;

  series_default_end_time: string;

  series_dates: SeriesDateEntry[];

  series_exceptions: SeriesException[];

  series_default_location?: string;

  series_default_room?: string;

  series_default_manual_location?: string;

  series_default_manual_location_address?: string;

  series_default_manual_room_capacity?: string | number;

}

export function toDateOnly(value?: string): string {

  if (!value) {

    return '';

  }

  return value.includes('T') ? value.split('T')[0] : value.substring(0, 10);

}

export function extractTimeFromDateTime(dateTimeString?: string): string {

  if (!dateTimeString) {

    return '12:00';

  }

  const trimmed = dateTimeString.trim();

  // Date-only values are UTC midnight in JS (YYYY-MM-DD → 02:00 in Danish summer time).
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {

    return '12:00';

  }

  try {

    const date = new Date(dateTimeString);

    if (Number.isNaN(date.getTime())) {

      return '12:00';

    }

    const hours = String(date.getHours()).padStart(2, '0');

    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;

  } catch {

    return '12:00';

  }

}

/** Split API datetime into separate form fields (date + start/end time). */

export function splitRowToFormFields(startDateTime?: string, endTime?: string): SeriesDateRow {

  return {

    sub_event_date: toDateOnly(startDateTime),

    sub_event_start_time: extractTimeFromDateTime(startDateTime),

    sub_event_end_time: (endTime || '13:00').substring(0, 5),

  };

}

function normalizeRowDateTime(row: SeriesDateRow): SeriesDateRow {

  const datePart = toDateOnly(row.sub_event_date);

  let startTime = row.sub_event_start_time?.substring(0, 5);

  if (!startTime && row.sub_event_date?.includes('T')) {

    startTime = extractTimeFromDateTime(row.sub_event_date);

  }

  startTime = startTime || '12:00';

  const endTime = (row.sub_event_end_time || '13:00').substring(0, 5);

  return {

    ...row,

    sub_event_date: combineDateAndTime(datePart, startTime),

    sub_event_start_time: startTime,

    sub_event_end_time: endTime,

  };

}

function isTruthySameAsPrevious(value: unknown): boolean {

  return value === true || value === 'true';

}

function isTruthySameCateringAsPrevious(value: unknown): boolean {

  return value === true || value === 'true';

}

function cateringKeyFromRow(row: SeriesDateRow): string {

  return String(row.sub_event_catering || 'irrelevant');

}

function parseSeriesDayCateringMap(
  data: Record<string, unknown>
): Record<string, string> {
  const raw = data['series_day_catering'];
  let parsed: unknown = raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [dateKey, value] of Object.entries(parsed as Record<string, unknown>)) {
    const date = toDateOnly(dateKey);
    const catering = String(value ?? '').trim();
    if (date && catering) {
      result[date] = catering;
    }
  }
  return result;
}

function applyPerDayCateringToRows(
  rows: SeriesDateRow[],
  data: Record<string, unknown>
): SeriesDateRow[] {
  const map = parseSeriesDayCateringMap(data);
  const sameCatering = data['series_same_catering'] !== 'false';
  const defaultCatering = String(
    data['series_default_catering'] || data['catering'] || 'irrelevant'
  );
  if (Object.keys(map).length === 0 && sameCatering) {
    return rows.map((row) => ({
      ...row,
      sub_event_catering: row.sub_event_catering || defaultCatering,
    }));
  }
  return rows.map((row) => {
    const date = toDateOnly(row.sub_event_date);
    let catering = date ? map[date] : undefined;
    if (!catering && sameCatering) {
      catering = defaultCatering;
    }
    if (!catering) {
      return row;
    }
    return { ...row, sub_event_catering: catering };
  });
}

/** Resolve per-day catering when rows use same_catering_as_previous. */

export function resolveSeriesRowCatering(
  rows: SeriesDateRow[],
  fallbackCatering: string
): SeriesDateRow[] {
  const resolved: SeriesDateRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row: SeriesDateRow = { ...rows[i] };
    if (i === 0) {
      row.sub_event_catering =
        row.sub_event_catering || fallbackCatering || 'irrelevant';
    } else if (isTruthySameCateringAsPrevious(row.same_catering_as_previous)) {
      const prev = resolved[i - 1];
      row.sub_event_catering = prev.sub_event_catering;
    } else if (!row.sub_event_catering) {
      row.sub_event_catering = fallbackCatering || 'irrelevant';
    }
    resolved.push(row);
  }
  return resolved;
}

export function applySeriesRowCateringToFormRows(
  rows: SeriesDateRow[],
  referenceRow?: SeriesDateRow
): SeriesDateRow[] {
  if (!rows.length) {
    return rows;
  }
  const chain = referenceRow ? [referenceRow, ...rows] : rows;
  const fallback =
    String(referenceRow?.sub_event_catering || 'irrelevant').trim() ||
    'irrelevant';
  const resolved = resolveSeriesRowCatering(
    chain.map((r, i) => ({
      ...r,
      same_catering_as_previous:
        i === 0 ? false : r.same_catering_as_previous,
    })),
    fallback
  );
  const offset = referenceRow ? 1 : 0;
  return rows.map((row, index) => {
    const abs = offset + index;
    const resolvedRow = resolved[abs];
    const prevRow = resolved[abs - 1];
    const same =
      abs > 0 &&
      cateringKeyFromRow(resolvedRow) === cateringKeyFromRow(prevRow);
    return {
      ...row,
      sub_event_catering: resolvedRow.sub_event_catering,
      same_catering_as_previous: referenceRow
        ? same
        : index === 0
          ? false
          : same,
    };
  });
}

/** Resolve per-day location when rows use same_as_previous. */

export function resolveSeriesRowLocations(rows: SeriesDateRow[]): SeriesDateRow[] {

  const resolved: SeriesDateRow[] = [];

  for (let i = 0; i < rows.length; i++) {

    const row: SeriesDateRow = { ...rows[i] };

    if (i > 0 && isTruthySameAsPrevious(row.same_as_previous)) {

      const prev = resolved[i - 1];

      row.sub_event_location = prev.sub_event_location;

      row.sub_event_room = prev.sub_event_room;

      row.sub_event_manual_location = prev.sub_event_manual_location;

      row.sub_event_manual_location_address = prev.sub_event_manual_location_address;

      row.sub_event_manual_room_capacity = prev.sub_event_manual_room_capacity;

    }

    resolved.push(row);

  }

  return resolved;

}

/** Infer backend type: same location on all days → multiple_dates, else linked group. */

export function inferApiSeriesDurationType(rows: SeriesDateRow[]): EventScheduleMode {

  if (!rows.length) {

    return 'multiple_dates';

  }

  const resolved = resolveSeriesRowLocations(rows);

  const firstKey = locationKeyFromRow(resolved[0]);

  const allSame = resolved.every((row) => locationKeyFromRow(row) === firstKey);

  return allSame ? 'multiple_dates' : 'multiple_dates_locations';

}

function copyRowLocationToParentFields(

  data: Record<string, unknown>,

  row: SeriesDateRow

): void {

  if (!row.sub_event_location) {

    return;

  }

  data['location'] = row.sub_event_location;

  data['room'] = row.sub_event_room ?? '';

  data['manual_location'] = row.sub_event_manual_location ?? '';

  data['manual_location_address'] = row.sub_event_manual_location_address ?? '';

  data['manual_room_capacity'] = row.sub_event_manual_room_capacity ?? '';

}

export function applySeriesRowLocationsToFormRows(
  rows: SeriesDateRow[],
  referenceRow?: SeriesDateRow
): SeriesDateRow[] {
  if (!rows.length) {
    return rows;
  }
  const chain = referenceRow ? [referenceRow, ...rows] : rows;
  const resolved = resolveSeriesRowLocations(
    chain.map((r, i) => ({
      ...r,
      same_as_previous: i === 0 ? false : r.same_as_previous,
    }))
  );
  const offset = referenceRow ? 1 : 0;
  return rows.map((row, index) => {
    const abs = offset + index;
    const resolvedRow = resolved[abs];
    const prevRow = resolved[abs - 1];
    const same =
      abs > 0 &&
      locationKeyFromRow(resolvedRow) === locationKeyFromRow(prevRow);
    return {
      ...row,
      sub_event_location: resolvedRow.sub_event_location,
      sub_event_room: resolvedRow.sub_event_room,
      sub_event_manual_location: resolvedRow.sub_event_manual_location,
      sub_event_manual_location_address:
        resolvedRow.sub_event_manual_location_address,
      sub_event_manual_room_capacity: resolvedRow.sub_event_manual_room_capacity,
      same_as_previous: referenceRow ? same : index === 0 ? false : same,
    };
  });
}

/**

 * Build API sub_events + main start/end from series panel rows (per-day times).

 */

export function buildSeriesEventDates(

  mode: EventScheduleMode,

  rows: SeriesDateRow[]

): { startDate: string; endDate: string; subEvents: SeriesDateRow[] } {

  if (!rows?.length) {

    return { startDate: '', endDate: '', subEvents: [] };

  }

  const normalized = rows.map((row) => normalizeRowDateTime(row));

  if (mode === 'multiple_dates_locations') {

    const first = normalized[0];

    const end = applyEndTimeToStartDate(first.sub_event_date, first.sub_event_end_time || '13:00');

    return {

      startDate: first.sub_event_date,

      endDate: end,

      subEvents: normalized,

    };

  }

  // multiple_dates: first row is parent event

  const first = normalized[0];

  const rest = normalized.slice(1);

  return {

    startDate: first.sub_event_date,

    endDate: applyEndTimeToStartDate(first.sub_event_date, first.sub_event_end_time || '13:00'),

    subEvents: rest,

  };

}

/** Merge parent date + sub-events into unified panel rows for edit form. */

/** Sort series rows chronologically (date + start time). */
export function sortSeriesDateRows(rows: SeriesDateRow[]): SeriesDateRow[] {
  return [...rows].sort((a, b) => {
    const aDt = combineDateAndTime(
      toDateOnly(a.sub_event_date),
      (a.sub_event_start_time || '12:00').substring(0, 5)
    );
    const bDt = combineDateAndTime(
      toDateOnly(b.sub_event_date),
      (b.sub_event_start_time || '12:00').substring(0, 5)
    );
    return new Date(aDt).getTime() - new Date(bDt).getTime();
  });
}

/** Map linked group API rows into guided edit form (all siblings, sorted). */
export function mapLinkedGroupScheduleForForm(
  eventData: { start_date?: string; end_date?: string; guid?: string },
  rows: SeriesDateRow[]
): {
  start_date: string;
  end_date: string;
  additional_dates: SeriesDateRow[];
  schedule_inferred_mode: GuidedScheduleInferredMode;
  primary_session_guid: string | null;
  series_schedule_editable: boolean;
} {
  const sorted = sortSeriesDateRows(rows);
  if (sorted.length <= 1) {
    const start = toDatetimeLocalInput(eventData.start_date);
    const end = toDatetimeLocalInput(eventData.end_date);
    return {
      start_date: start,
      end_date: end,
      additional_dates: [],
      schedule_inferred_mode: isSameCalendarDay(start, end) ? 'single_day' : 'multi_day',
      primary_session_guid: sorted[0]?.sub_event_guid || eventData.guid || null,
      series_schedule_editable: false,
    };
  }

  const guided = mapApiRowsToGuidedForm(
    'multiple_dates_locations',
    eventData.start_date,
    eventData.end_date,
    sorted
  );

  return {
    ...guided,
    primary_session_guid: sorted[0]?.sub_event_guid || null,
    series_schedule_editable: true,
  };
}

/**
 * Re-normalize a series row without re-parsing a date-only string as UTC midnight.
 * Rows from mapSubEventsForForm already have sub_event_start_time / sub_event_end_time.
 */
function keepSeriesRowClockTimes(se: SeriesDateRow, fallbackEndTime?: string): SeriesDateRow {
  const dateSource = se.sub_event_date || '';
  const hasClockInDate = /[T ]\d{1,2}:\d{2}/.test(dateSource);
  const existingStart = se.sub_event_start_time?.substring(0, 5);
  const startTime =
    existingStart ||
    (hasClockInDate ? extractTimeFromDateTime(dateSource) : '12:00');
  const endTime = (se.sub_event_end_time || fallbackEndTime || '13:00').substring(0, 5);

  return {
    ...se,
    sub_event_date: toDateOnly(dateSource),
    sub_event_start_time: startTime,
    sub_event_end_time: endTime,
  };
}

export function mergeSeriesRowsForForm(

  mode: EventScheduleMode,

  startDate?: string,

  endDate?: string,

  subEvents?: SeriesDateRow[]

): { rows: SeriesDateRow[] } {

  if (mode === 'multiple_dates_locations') {

    const fallbackEnd = extractTimeFromDateTime(endDate);

    const rows = (subEvents || []).map((se) => keepSeriesRowClockTimes(se, fallbackEnd));

    return { rows };

  }

  if (mode === 'multiple_dates') {

    const parentRow: SeriesDateRow = {

      ...splitRowToFormFields(startDate, endDate),

    };

    const childRows = (subEvents || []).map((se) => keepSeriesRowClockTimes(se));

    return { rows: [parentRow, ...childRows] };

  }

  return { rows: [] };

}

export function getSeriesPlanningMode(data: Record<string, unknown>): SeriesPlanningMode {

  const mode = data['series_planning_mode'];

  if (mode === 'manual' || mode === 'standard_with_exceptions') {

    return mode;

  }

  return 'standard_with_exceptions';

}

function normalizeTime(value?: string): string {

  return (value || '12:00').substring(0, 5);

}

function locationKeyFromRow(row: SeriesDateRow | SeriesLocationDefaults): string {

  const loc =

    'sub_event_location' in row && row.sub_event_location !== undefined

      ? row.sub_event_location

      : ('location' in row ? row.location : '') || '';

  if (loc === 'manual') {

    const name =

      'sub_event_manual_location' in row && row.sub_event_manual_location !== undefined

        ? row.sub_event_manual_location

        : ('manual_location' in row ? row.manual_location : '') || '';

    const address =

      'sub_event_manual_location_address' in row && row.sub_event_manual_location_address !== undefined

        ? row.sub_event_manual_location_address

        : ('manual_location_address' in row ? row.manual_location_address : '') || '';

    return `manual:${name}:${address}`;

  }

  const room =

    'sub_event_room' in row && row.sub_event_room !== undefined

      ? row.sub_event_room

      : ('room' in row ? row.room : '') || '';

  return `${loc}:${room}`;

}

interface SeriesLocationDefaults {

  location?: string;

  room?: string;

  manual_location?: string;

  manual_location_address?: string;

  manual_room_capacity?: string | number;

}

function applyLocationDefaultsToRow(row: SeriesDateRow, defaults: SeriesLocationDefaults): void {

  if (!defaults.location) {

    return;

  }

  row.sub_event_location = defaults.location;

  if (defaults.location === 'manual') {

    row.sub_event_manual_location = defaults.manual_location;

    row.sub_event_manual_location_address = defaults.manual_location_address;

    row.sub_event_manual_room_capacity = defaults.manual_room_capacity;

    return;

  }

  row.sub_event_room = defaults.room;

  row.sub_event_manual_room_capacity = defaults.manual_room_capacity;

}

function readSeriesDefaultLocation(data: Record<string, unknown>): SeriesLocationDefaults {

  return {

    location: data['series_default_location'] as string | undefined,

    room: data['series_default_room'] as string | undefined,

    manual_location: data['series_default_manual_location'] as string | undefined,

    manual_location_address: data['series_default_manual_location_address'] as string | undefined,

    manual_room_capacity: data['series_default_manual_room_capacity'] as string | number | undefined,

  };

}

function readExceptionLocation(exc: SeriesException): SeriesLocationDefaults | null {

  if (!exc.exception_location) {

    return null;

  }

  return {

    location: exc.exception_location,

    room: exc.exception_room,

    manual_location: exc.exception_manual_location,

    manual_location_address: exc.exception_manual_location_address,

    manual_room_capacity: exc.exception_manual_room_capacity,

  };

}

function findExceptionForDate(exceptions: SeriesException[], dateOnly: string): SeriesException | undefined {

  return exceptions.find((exc) => toDateOnly(exc.exception_date) === dateOnly);

}

function isTruthySkip(value: unknown): boolean {

  return value === true || value === 'true';

}

function isSkipException(exc: SeriesException | undefined): boolean {

  if (!exc) {

    return false;

  }

  return isTruthySkip(exc.exception_skip);

}

function hasTimeOverride(exc: SeriesException): boolean {

  return !!(exc.exception_start_time || exc.exception_end_time);

}

function hasLocationOverride(exc: SeriesException): boolean {

  return !!exc.exception_location;

}

function findGuidForExpandedRow(

  dates: SeriesDateEntry[],

  exceptions: SeriesException[],

  dateOnly: string

): string | undefined {

  const exc = findExceptionForDate(exceptions, dateOnly);

  if (exc?.exception_sub_event_guid) {

    return exc.exception_sub_event_guid;

  }

  const entry = dates.find((d) => toDateOnly(d.date) === dateOnly);

  return entry?.series_date_guid;

}

/** Expand standard schedule + exceptions into per-day sub_events rows. */

export function expandStandardPlanToSubEvents(

  mode: EventScheduleMode,

  data: Record<string, unknown>

): SeriesDateRow[] {

  const dates = ((data['series_dates'] as SeriesDateEntry[]) || [])

    .map((entry) => ({

      date: toDateOnly(entry.date),

      series_date_guid: entry.series_date_guid,

    }))

    .filter((entry) => entry.date)

    .sort((a, b) => a.date.localeCompare(b.date));

  if (!dates.length) {

    return [];

  }

  const defaultStart = normalizeTime(String(data['series_default_start_time'] || '09:00'));

  const defaultEnd = normalizeTime(String(data['series_default_end_time'] || '16:00'));

  const dayTimes = parseSeriesDayTimesMap(data);

  const dayLocations = parseSeriesDayLocationsMap(data);

  const exceptions = (data['series_exceptions'] as SeriesException[]) || [];

  const defaultLocation = readSeriesDefaultLocation(data);

  return dates.flatMap((entry) => {

    const exc = findExceptionForDate(exceptions, entry.date);

    if (isSkipException(exc)) {

      return [];

    }

    const dayTime = dayTimes[entry.date];

    const startTime = exc?.exception_start_time

      ? normalizeTime(exc.exception_start_time)

      : dayTime?.start
        ? normalizeTime(dayTime.start)
        : defaultStart;

    const endTime = exc?.exception_end_time
      ? normalizeTime(exc.exception_end_time)
      : dayTime?.end
        ? normalizeTime(dayTime.end)
        : defaultEnd;

    const row: SeriesDateRow = {

      sub_event_guid: findGuidForExpandedRow(dates, exceptions, entry.date),

      sub_event_date: entry.date,

      sub_event_start_time: startTime,

      sub_event_end_time: endTime,

    };

    const excLocation = exc && hasLocationOverride(exc) ? readExceptionLocation(exc) : null;

    const dayLocation = dayLocations[entry.date];

    if (excLocation) {

      applyLocationDefaultsToRow(row, excLocation);

    } else if (dayLocation?.location) {

      applyLocationDefaultsToRow(row, dayLocation);

    } else if (defaultLocation?.location) {

      applyLocationDefaultsToRow(row, defaultLocation);

    }

    if (exc?.exception_additional_rooms?.length) {
      row.sub_event_additional_rooms = exc.exception_additional_rooms.map((entry) => ({ ...entry }));
    }

    return [row];

  });

}

interface RowConfigKey {

  start: string;

  end: string;

  locKey: string;

}

function rowConfigKey(mode: EventScheduleMode, row: SeriesDateRow): RowConfigKey {

  return {

    start: normalizeTime(row.sub_event_start_time),

    end: normalizeTime(row.sub_event_end_time),

    locKey: row.sub_event_location ? locationKeyFromRow(row) : '',

  };

}

/** Infer whether loaded rows fit standard+exceptions or per-day manual editing. */

export function inferPlanningMode(mode: EventScheduleMode, rows: SeriesDateRow[]): SeriesPlanningMode {

  if (rows.length <= 1) {

    return 'standard_with_exceptions';

  }

  const keys = rows.map((row) => {

    const cfg = rowConfigKey(mode, row);

    return `${cfg.start}|${cfg.end}|${cfg.locKey}`;

  });

  const freq = new Map<string, number>();

  for (const key of keys) {

    freq.set(key, (freq.get(key) || 0) + 1);

  }

  const maxFreq = Math.max(...freq.values());

  if (maxFreq === 1 && rows.length > 2) {

    return 'manual';

  }

  if (rows.length > 3 && maxFreq / rows.length < 0.34) {

    return 'manual';

  }

  return 'standard_with_exceptions';

}

/** Collapse per-day rows into standard schedule fields for the edit form. */

export function collapseSubEventsToStandardPlan(

  mode: EventScheduleMode,

  rows: SeriesDateRow[]

): SeriesStandardPlan {

  const empty: SeriesStandardPlan = {

    planningMode: 'standard_with_exceptions',

    series_default_start_time: '09:00',

    series_default_end_time: '16:00',

    series_dates: [],

    series_exceptions: [],

  };

  if (!rows.length) {

    return empty;

  }

  const planningMode = inferPlanningMode(mode, rows);

  if (planningMode === 'manual') {

    return { ...empty, planningMode: 'manual' };

  }

  const configCounts = new Map<string, { count: number; sample: SeriesDateRow }>();

  for (const row of rows) {

    const cfg = rowConfigKey(mode, row);

    const key = `${cfg.start}|${cfg.end}|${cfg.locKey}`;

    const existing = configCounts.get(key);

    if (existing) {

      existing.count += 1;

    } else {

      configCounts.set(key, { count: 1, sample: row });

    }

  }

  let dominantKey = '';

  let dominantCount = 0;

  for (const [key, value] of configCounts.entries()) {

    if (value.count > dominantCount) {

      dominantKey = key;

      dominantCount = value.count;

    }

  }

  const dominantSample = configCounts.get(dominantKey)?.sample || rows[0];

  const dominantCfg = rowConfigKey(mode, dominantSample);

  const series_dates: SeriesDateEntry[] = [];

  const series_exceptions: SeriesException[] = [];

  for (const row of rows) {

    const date = toDateOnly(row.sub_event_date);

    series_dates.push({ date, series_date_guid: row.sub_event_guid });

    const cfg = rowConfigKey(mode, row);

    const differs =

      cfg.start !== dominantCfg.start ||

      cfg.end !== dominantCfg.end ||

      (cfg.locKey && dominantCfg.locKey && cfg.locKey !== dominantCfg.locKey);

    if (!differs) {

      continue;

    }

    const exc: SeriesException = {

      exception_date: date,

      exception_sub_event_guid: row.sub_event_guid,

    };

    if (cfg.start !== dominantCfg.start) {

      exc.exception_start_time = cfg.start;

    }

    if (cfg.end !== dominantCfg.end) {

      exc.exception_end_time = cfg.end;

    }

    if (mode === 'multiple_dates_locations' || (cfg.locKey && cfg.locKey !== dominantCfg.locKey)) {

      exc.exception_location = row.sub_event_location;

      exc.exception_room = row.sub_event_room;

      exc.exception_manual_location = row.sub_event_manual_location;

      exc.exception_manual_location_address = row.sub_event_manual_location_address;

      exc.exception_manual_room_capacity = row.sub_event_manual_room_capacity;

    }

    if (hasTimeOverride(exc) || hasLocationOverride(exc)) {

      series_exceptions.push(exc);

    }

  }

  const plan: SeriesStandardPlan = {

    planningMode: 'standard_with_exceptions',

    series_default_start_time: dominantCfg.start,

    series_default_end_time: dominantCfg.end,

    series_dates,

    series_exceptions,

  };

  if (mode === 'multiple_dates' || mode === 'multiple_dates_locations') {

    plan.series_default_location = dominantSample.sub_event_location;

    plan.series_default_room = dominantSample.sub_event_room;

    plan.series_default_manual_location = dominantSample.sub_event_manual_location;

    plan.series_default_manual_location_address = dominantSample.sub_event_manual_location_address;

    plan.series_default_manual_room_capacity = dominantSample.sub_event_manual_room_capacity;

  }

  return plan;

}

const SERIES_PLANNING_FORM_KEYS = [

  'series_planning_mode',

  'series_default_start_time',

  'series_default_end_time',

  'series_dates',

  'series_exceptions',

  'series_default_location',

  'series_default_room',

  'series_default_manual_location',

  'series_default_manual_location_address',

  'series_default_manual_room_capacity',

  'series_day_times',

  'series_day_locations',

  'series_same_time',

  'series_same_location',

  'series_same_modules',

  'series_default_module_ids',

  'series_default_subtype_ids',

  'series_same_catering',

  'series_default_catering',

  'series_day_catering',

] as const;

const GUIDED_FORM_KEYS = [

  'schedule_inferred_mode',

  'additional_dates',

  'schedule_mode',

  'series_planner_active',

  'series_day_modules',

  'event_series_planner',

  ...SERIES_PLANNING_FORM_KEYS,

] as const;

function stripGuidedPlanningFields(data: Record<string, unknown>): void {

  for (const key of GUIDED_FORM_KEYS) {

    delete data[key];

  }

}

function stripSeriesPlanningFields(data: Record<string, unknown>): void {

  stripGuidedPlanningFields(data);

}

/** Normalize raw form data before add/update API calls. */

export function normalizeEventFormForApi(data: Record<string, unknown>): Record<string, unknown> {

  const out = { ...data };

  const mode = inferGuidedScheduleMode(out);

  out['event_duration_type'] = mode;

  out['series_share_participants'] = getSeriesShareParticipants(out);

  if (mode === 'single_day') {

    const start = String(out['start_date'] || '');

    const rawEndTime = out['end_time'];
    const hasExplicitEndTime =
      rawEndTime != null && String(rawEndTime).trim() !== '';
    const rawEndDate = out['end_date'];
    const hasEndDate =
      rawEndDate != null && String(rawEndDate).trim() !== '';

    let endTime: string;
    if (hasExplicitEndTime && !hasEndDate) {
      endTime = normalizeTime(String(rawEndTime));
    } else if (hasEndDate) {
      endTime = extractTimeFromDateTime(String(rawEndDate));
    } else {
      endTime = extractTimeFromDateTime(start);
    }

    out['end_date'] = applyEndTimeToStartDate(start, endTime);

    out['end_time'] = endTime;

    stripGuidedPlanningFields(out);

    return out;

  }

  if (mode === 'multi_day') {

    const rawSkips = (out['multi_day_skip_dates'] as Array<{ skip_date?: string }>) || [];

    const fromPanel = rawSkips

      .map((entry) => toDateOnly(entry.skip_date))

      .filter(Boolean);

    const exceptions = (out['series_exceptions'] as SeriesException[]) || [];

    const fromExceptions = exceptions

      .filter(isSkipException)

      .map((exc) => toDateOnly(exc.exception_date))

      .filter(Boolean);

    out['multi_day_skip_dates'] = Array.from(

      new Set([...fromPanel, ...fromExceptions])

    );

    stripGuidedPlanningFields(out);

    return out;

  }

  if (mode === 'multiple_dates' || mode === 'multiple_dates_locations') {

    if (!inferScheduleCrossesDays(out)) {
      out['series_exceptions'] = [];
    }

    let rows = buildSeriesRowsFromGuidedForm(out);

    const apiType = mode;

    const built = buildSeriesEventDates(apiType, rows);

    out['start_date'] = built.startDate;

    out['end_date'] = built.endDate;

    out['sub_events'] = built.subEvents;

    out['event_duration_type'] = apiType;

    if (apiType === 'multiple_dates' && rows[0]) {

      copyRowLocationToParentFields(out, rows[0]);

    }

    stripGuidedPlanningFields(out);

    return out;

  }

  stripGuidedPlanningFields(out);

  return out;

}

